import React from "react";
import { spring, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { SvgIcon } from "../../components/SvgComponents";

interface IntroProps {
  headline: string;
  keywords?: string[];
  introIcon?: string;
}

export const ReactbitsGlowIntro: React.FC<IntroProps> = ({
  headline,
  keywords = [],
  introIcon,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // spring for brackets expanding
  const bracketScale = spring({
    frame,
    fps,
    from: 0.8,
    to: 1.0,
    config: { damping: 14, mass: 0.6, stiffness: 100 },
  });

  const bracketOpacity = interpolate(frame, [0, 10], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Words list
  const words = headline.split(" ");

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100%",
        padding: "0 60px",
        boxSizing: "border-box",
      }}
    >
      {/* Intro icon rendering */}
      {introIcon && (
        <div
          style={{
            marginBottom: "40px",
            transform: `scale(${spring({ frame, fps, from: 0, to: 1, config: { damping: 12 } })})`,
            filter: "drop-shadow(0 0 15px rgba(0,242,254,0.4))",
          }}
        >
          <SvgIcon name={introIcon} width={120} height={120} primaryColor="#00f2fe" />
        </div>
      )}

      {/* Main Hook Card */}
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          padding: "50px 30px",
          background: "rgba(6, 7, 19, 0.45)",
          border: "1.5px solid rgba(0, 242, 254, 0.2)",
          borderRadius: "32px",
          boxShadow: "0 20px 50px rgba(0,0,0,0.3), 0 0 30px rgba(0,242,254,0.05)",
          transform: `scale(${bracketScale})`,
          opacity: bracketOpacity,
          boxSizing: "border-box",
        }}
      >
        {/* Floating background neon dot */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "350px",
            height: "350px",
            background: "radial-gradient(circle, rgba(0,242,254,0.2) 0%, transparent 70%)",
            transform: "translate(-50%, -50%)",
            filter: "blur(50px)",
            zIndex: -1,
            pointerEvents: "none",
          }}
        />

        {/* Headline words rendering */}
        <h1
          style={{
            fontFamily: "var(--font-title)",
            fontSize: "90px",
            fontWeight: 900,
            color: "#ffffff",
            textAlign: "center",
            margin: 0,
            lineHeight: 1.05,
            letterSpacing: "-2px",
            textShadow: "0 10px 40px rgba(0, 0, 0, 0.6)",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignContent: "center",
          }}
        >
          {words.map((word, idx) => {
            const wordElapsed = Math.max(0, frame - idx * 2);
            const wordScale = spring({
              frame: wordElapsed,
              fps,
              from: 0,
              to: 1,
              config: { damping: 12, mass: 0.5, stiffness: 120 },
            });
            const wordTranslateY = interpolate(wordElapsed, [0, 8], [40, 0], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            const wordOpacity = interpolate(wordElapsed, [0, 6], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });

            // Make the last word colored with neon pink
            const isLast = idx === words.length - 1;

            return (
              <span
                key={idx}
                style={{
                  display: "inline-block",
                  margin: "0 12px",
                  transform: `scale(${wordScale}) translateY(${wordTranslateY}px)`,
                  opacity: wordOpacity,
                  background: isLast ? "linear-gradient(135deg, #00f2fe 30%, #ff007f 100%)" : "none",
                  WebkitBackgroundClip: isLast ? "text" : "none",
                  WebkitTextFillColor: isLast ? "transparent" : "none",
                  backgroundClip: isLast ? "text" : "none",
                  textShadow: isLast ? "0 0 20px rgba(0, 242, 254, 0.4)" : "none",
                }}
              >
                {word}
              </span>
            );
          })}
        </h1>
      </div>

      {/* Floating keyword pills below the card */}
      {keywords.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            justifyContent: "center",
            marginTop: "40px",
            opacity: interpolate(frame, [15, 25], [0, 1], { extrapolateLeft: "clamp" }),
          }}
        >
          {keywords.map((keyword, i) => (
            <div
              key={i}
              style={{
                background: "rgba(6, 7, 19, 0.6)",
                border: "1px solid rgba(255, 0, 127, 0.3)",
                padding: "8px 20px",
                borderRadius: "50px",
                fontFamily: "var(--font-title)",
                fontSize: "16px",
                fontWeight: 700,
                color: "#ff007f",
                textTransform: "uppercase",
                letterSpacing: "1.5px",
                boxShadow: "0 0 10px rgba(255, 0, 127, 0.1)",
              }}
            >
              #{keyword}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
